import React, { useState } from 'react';
import Button from '../Button/Button';
import { ReactComponent as DocumentsIcon } from "../../assets/icons/document.svg";
import { ReactComponent as ViewMoreIcon } from "../../assets/icons/view-more.svg";
import './MesDocuments.css';
import { useAuth } from '../../contexts/AuthContext'; // Assurez-vous que ce chemin est correct

interface Document {
  id: string;
  type: string;
  name: string;
  file?: File;
  url?: string;
}

const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB

const MesDocuments = () => {
  const { user } = useAuth();
  const userRole = user?.role || '';
  const isResponsableAchat = userRole === 'responsable_achat';
  const isPrestataire = userRole === 'interlocuteur' || userRole === 'gestionnaire';

  const [documents, setDocuments] = useState<Record<string, Document[]>>({
    logo: [],
    piecesMarche: [
      { id: '1', type: 'contrat', name: 'Contrat.pdf', url: '/path/to/contrat.pdf' },
      { id: '2', type: 'cctp', name: 'CCTP.pdf', url: '/path/to/cctp.pdf' },
      { id: '3', type: 'rib', name: 'RIB.pdf', url: '/path/to/rib.pdf' }
    ],
    assurances: [],
    attestations: [],
    certifications: []
  });

  // Logo upload handler
  const handleLogoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.jpeg,.jpg,.png';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
          alert('Format accepté : jpeg ou png');
          return;
        }
        if (file.size > MAX_LOGO_SIZE) {
          alert('Logo trop lourd (max 2Mo)');
          return;
        }
        setDocuments(prev => ({
          ...prev,
          logo: [{ id: Date.now().toString(), type: 'logo', name: file.name, file }]
        }));
      }
    };
    input.click();
  };

  // Generic upload handler for sections that allow adding documents
  const handleFileUpload = (section: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpeg,.jpg,.png';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setDocuments(prev => ({
          ...prev,
          [section]: [...prev[section], { id: Date.now().toString(), type: section, name: file.name, file }]
        }));
      }
    };
    input.click();
  };

  // Replace handler for attestation (only one file)
  const handleAttestationReplace = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setDocuments(prev => ({
          ...prev,
          attestations: [{ id: Date.now().toString(), type: 'attestation', name: file.name, file }]
        }));
      }
    };
    input.click();
  };

  const handleDownload = (document: Document) => {
    // Handle document download using url or file
    alert('Téléchargement : ' + document.name);
  };

  // Si l'utilisateur est un acheteur ou un validateur, ne pas afficher le composant
  if (userRole === 'acheteur' || userRole === 'validateur') {
    return null;
  }

  return (
    <div className="mes-documents">
      {/* Section Logo - visible pour tous les rôles qui ont accès à l'onglet */}
      <section className="document-section">
        <h2 className='section-header'>Logo de l'entreprise</h2>
        <div className="section-content">
          <div className="logo-placeholder">
            {documents.logo.length > 0 ? (
              <img
                src={documents.logo[0].file ? URL.createObjectURL(documents.logo[0].file) : documents.logo[0].url}
                alt="Logo"
              />
            ) : (
              <div>
                Aucun logo
              </div>
            )}
          </div>
          <div className="add-document-container">
            <Button value={documents.logo.length > 0 ? "Remplacer le logo" : "Déposer un logo"} variant="primary" size="small" onClick={handleLogoUpload} />
          </div>
        </div>
      </section>

      {/* Les sections suivantes ne sont visibles que pour les prestataires (interlocuteur, gestionnaire) */}
      {isPrestataire && (
        <>
          {/* Section Pièces du marché */}
          <section className="document-section">
            <h2 className='section-header'>Pièces du marché</h2>
            <div className="section-content">
              <div className="documents-grid">
                {documents.piecesMarche.map((doc) => (
                  <div key={doc.id} className="document-card">
                    <div className="document-content">
                      <DocumentsIcon />
                      <div className="document-name">
                        {doc.name}
                        <ViewMoreIcon className="view-more-icon" onClick={() => handleDownload(doc)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="info-text">
                Ces documents ne sont pas modifiables.
              </div>
            </div>
          </section>

          {/* Section Assurances */}
          <section className="document-section">
            <h2 className='section-header'>Assurances</h2>
            <div className="section-content">
              <div className="documents-grid">
                {documents.assurances.map((doc) => (
                  <div key={doc.id} className="document-card">
                    <div className="document-content">
                      <DocumentsIcon />
                      <div className="document-name">
                        {doc.name}
                        <ViewMoreIcon className="view-more-icon" onClick={() => handleDownload(doc)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {documents.assurances.length === 0 && (
                <div className="info-text">Aucune assurance déposée</div>
              )}
              <div className="add-document-container">
                <Button
                  value="Mettre à jour"
                  variant="primary"
                  size="small"
                  onClick={() => handleFileUpload('assurances')}
                />
              </div>
            </div>
          </section>

          {/* Section Attestation fiscale et sociale */}
          <section className="document-section">
            <h2 className='section-header'>Attestation fiscale et sociale</h2>
            <div className="section-content">
              <div className="documents-grid">
                {documents.attestations.length > 0 ? (
                  documents.attestations.map((doc) => (
                    <div key={doc.id} className="document-card">
                      <div className="document-content">
                        <DocumentsIcon />
                        <div className="document-name">
                          {doc.name}
                          <ViewMoreIcon className="view-more-icon" onClick={() => handleDownload(doc)} />
                        </div>
                      </div>
                    </div>
                  ))
                ) : null}
              </div>
              {documents.attestations.length === 0 && (
                <div className="info-text">Aucune attestation déposée</div>
              )}
              <div className="add-document-container">
                <Button
                  value="Mettre à jour"
                  variant="primary"
                  size="small"
                  onClick={handleAttestationReplace}
                />
              </div>
            </div>
          </section>

          {/* Section Certifications et attestations */}
          <section className="document-section">
            <h2 className='section-header'>Certifications et attestations</h2>
            <div className="section-content">
              <div className="documents-grid">
                {documents.certifications.map((doc) => (
                  <div key={doc.id} className="document-card">
                    <div className="document-content">
                      <DocumentsIcon />
                      <div className="document-name">
                        {doc.name}
                        <ViewMoreIcon className="view-more-icon" onClick={() => handleDownload(doc)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {documents.certifications.length === 0 && (
                <div className="info-text">Aucune certification ou attestation déposée</div>
              )}
              <div className="add-document-container">
                <Button
                  value="Mettre à jour"
                  variant="primary"
                  size="small"
                  onClick={() => handleFileUpload('certifications')}
                />
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default MesDocuments;